import { useRef, useEffect, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { useIntl } from 'react-intl';
import Quill from 'quill';
import {
  mapControlsToQuillToolbar,
  getFormatDetails,
  getIconKeyFromSelector,
  createButtonContainer,
  createCustomButtonWrapper,
} from '../utils/quillUtils';
import { muiIconComponents, STANDARD_BUTTON_SELECTORS } from '../constants/editorConstants';

export interface DynamicButtonInfo {
  root: Root;
  buttonElement: HTMLElement;
  iconKey: keyof typeof muiIconComponents;
  formatAction: (quill: Quill, currentActiveState: boolean) => void;
  checkActive: (formats: Record<string, any>) => boolean;
}

interface UseQuillEditorOptions {
  id: string;
  editingOptions: string[];
  placeholder?: string;
  value?: string;
  onChange: (html: string) => void;
  onEmojiButtonClick: (element: HTMLElement) => void;
}

export const useQuillEditor = ({
  id,
  editingOptions,
  placeholder,
  value,
  onChange,
  onEmojiButtonClick,
}: UseQuillEditorOptions) => {
  const intl = useIntl();
  const quillRef = useRef<HTMLDivElement>(null);
  const quillInstance = useRef<Quill | null>(null);
  const dynamicButtonRoots = useRef<DynamicButtonInfo[]>([]);
  const [activeFormats, setActiveFormats] = useState<Record<string, any>>({});
  const isInternalChangeRef = useRef<boolean>(false);

  // Initialize Quill editor
  useEffect(() => {
    if (!quillRef.current) {
      return;
    }

    if (!quillInstance.current) {
      quillRef.current.innerHTML = '';

      const defaultPlaceholder = intl.formatMessage({ id: 'WYSIWYG.PLACEHOLDER' });

      const quill = new Quill(quillRef.current, {
        theme: 'snow',
        placeholder: placeholder || defaultPlaceholder,
        modules: {
          toolbar: mapControlsToQuillToolbar(editingOptions),
        },
      });

      quillInstance.current = quill;

      // Set initial content and formats
      if (value) {
        const delta = quill.clipboard.convert(value as any);
        quill.setContents(delta, 'silent');
      }

      const initialRange = quill.getSelection() || { index: 0, length: 0 };
      setActiveFormats(quill.getFormat(initialRange));

      // Setup toolbar
      setupToolbar(quill);

      // Setup event handlers
      setupEventHandlers(quill);
    }

    return () => {
      cleanup();
    };
  }, [id, JSON.stringify(editingOptions)]);

  // Handle placeholder updates
  useEffect(() => {
    if (quillInstance.current) {
      const defaultPlaceholder = intl.formatMessage({ id: 'WYSIWYG.PLACEHOLDER' });
      quillInstance.current.root.dataset.placeholder = placeholder || defaultPlaceholder;
    }
  }, [placeholder, intl]);

  // Handle value updates
  useEffect(() => {
    if (
      quillInstance.current &&
      typeof value === 'string' &&
      value !== quillInstance.current.root.innerHTML &&
      !isInternalChangeRef.current
    ) {
      const currentSelection = quillInstance.current.getSelection();
      const wasAtEnd =
        currentSelection && currentSelection.index === quillInstance.current.getLength() - 1;

      // Use Quill's setText/setContents method instead of directly setting innerHTML
      // This preserves Quill's internal state better
      if (value === '') {
        quillInstance.current.setText('');
      } else {
        // For non-empty content, we still need to use innerHTML to preserve formatting
        quillInstance.current.root.innerHTML = value;
      }

      // Only restore selection if we have a valid range and the document length supports it
      if (currentSelection) {
        // Use setTimeout to ensure Quill has processed the content change
        setTimeout(() => {
          if (!quillInstance.current) return;

          try {
            const newLength = quillInstance.current.getLength();
            const safeIndex = Math.min(currentSelection.index, Math.max(0, newLength - 1));
            const safeLength = Math.min(currentSelection.length, newLength - safeIndex);

            // If the user was at the end before, keep them at the end
            if (wasAtEnd) {
              quillInstance.current.setSelection(newLength - 1, 0, 'silent');
            } else {
              quillInstance.current.setSelection(safeIndex, safeLength, 'silent');
            }
          } catch {
            // If selection restoration fails, place cursor at the end safely
            try {
              const length = quillInstance.current.getLength();
              quillInstance.current.setSelection(Math.max(0, length - 1), 0, 'silent');
            } catch {
              // If even that fails, just ignore the selection restoration
            }
          }
        }, 0);
      }
    }
  }, [value]);

  const setupToolbar = (quill: Quill) => {
    const toolbarModule = quill.getModule('toolbar') as any;
    if (!toolbarModule?.container) return;

    const toolbarContainer = toolbarModule.container as HTMLElement;
    const currentDynamicButtonRoots: DynamicButtonInfo[] = [];

    // Process standard Quill buttons
    STANDARD_BUTTON_SELECTORS.forEach((selector) => {
      const originalButtonEl = toolbarContainer.querySelector(selector) as HTMLElement | null;
      if (originalButtonEl?.parentNode) {
        const spanContainer = createButtonContainer(originalButtonEl);
        originalButtonEl.parentNode.replaceChild(spanContainer, originalButtonEl);

        const iconKey = getIconKeyFromSelector(selector);
        const formatDetails = getFormatDetails(selector, intl);

        if (iconKey && formatDetails && muiIconComponents[iconKey]) {
          const root = createRoot(spanContainer);
          currentDynamicButtonRoots.push({
            root,
            buttonElement: spanContainer,
            iconKey,
            ...formatDetails,
          });
        }
      }
    });

    // Add custom title button first
    if (editingOptions.includes('title')) {
      const titleWrapper = createCustomButtonWrapper('ql-title-wrapper');
      const titleSpanContainer = createButtonContainer(document.createElement('span'), 'ql-title');

      toolbarContainer.insertBefore(titleWrapper, toolbarContainer.firstChild);
      titleWrapper.appendChild(titleSpanContainer);

      const root = createRoot(titleSpanContainer);
      currentDynamicButtonRoots.push({
        root,
        buttonElement: titleSpanContainer,
        iconKey: 'title',
        formatAction: (q, isActive) => q.format('header', isActive ? false : 2),
        checkActive: (formats) => formats.header === 2,
      });
    }

    // Add custom emoji button
    if (editingOptions.includes('emoji')) {
      const emojiWrapper = createCustomButtonWrapper('ql-emoji-wrapper');
      emojiWrapper.style.order = '999';

      const emojiButton = document.createElement('button');
      emojiButton.innerHTML = '😀';
      emojiButton.className = 'ql-emoji wysiwyg-emoji-button';
      emojiButton.setAttribute('type', 'button');
      emojiButton.style.cssText =
        'font-size: 1.2em; line-height: 1; padding: 0; width: 40px; height: 40px; border-radius: 50%; border: none; background: none; cursor: pointer; display: flex; align-items: center; justify-content: center; box-sizing: border-box; transition: background-color 0.2s ease;';

      emojiButton.addEventListener('click', (e) => {
        e.preventDefault();
        onEmojiButtonClick(e.currentTarget as HTMLElement);
      });

      emojiWrapper.appendChild(emojiButton);
      toolbarContainer.appendChild(emojiWrapper);
    }

    dynamicButtonRoots.current = currentDynamicButtonRoots;
  };

  const setupEventHandlers = (quill: Quill) => {
    const handleTextChange = () => {
      if (quillInstance.current) {
        // Set flag to indicate this is an internal change
        isInternalChangeRef.current = true;

        const html = quillInstance.current.root.innerHTML;
        const isEmpty = quillInstance.current.getText().trim().length === 0;
        onChange(isEmpty ? '' : html);

        const currentRange = quillInstance.current.getSelection();
        if (currentRange) {
          setActiveFormats(quillInstance.current.getFormat(currentRange));
        } else {
          setActiveFormats(quillInstance.current.getFormat(0));
        }

        // Reset flag after a short delay to allow the value prop update to be processed
        setTimeout(() => {
          isInternalChangeRef.current = false;
        }, 0);
      }
    };

    quill.on('text-change', handleTextChange);
    (quill as any)._handleTextChange = handleTextChange;

    quill.on('selection-change', (range) => {
      if (range) {
        setActiveFormats(quill.getFormat(range));
      } else {
        if (quill.hasFocus()) {
          setActiveFormats(quill.getFormat(0));
        } else {
          const currentFormats = quill.getFormat(0);
          if (Object.keys(currentFormats).length > 0 || quill.getLength() <= 1) {
            setActiveFormats(currentFormats);
          }
        }
      }
    });
  };

  const cleanup = () => {
    dynamicButtonRoots.current.forEach((info) => info.root.unmount());
    dynamicButtonRoots.current = [];

    if (quillInstance.current) {
      const currentQuill = quillInstance.current;
      if ((currentQuill as any)._handleTextChange) {
        currentQuill.off('text-change', (currentQuill as any)._handleTextChange);
      }

      const toolbarModule = currentQuill.getModule('toolbar') as any;
      if (toolbarModule?.container instanceof HTMLElement) {
        toolbarModule.container.remove();
      }
      quillInstance.current = null;
    }
  };

  const focus = () => {
    if (quillInstance.current) {
      quillInstance.current.focus();
    }
  };

  const getEditor = () => quillInstance.current;

  return {
    quillRef,
    quillInstance: quillInstance.current,
    activeFormats,
    dynamicButtonRoots: dynamicButtonRoots.current,
    focus,
    getEditor,
  };
};
